"use client"

import { useState, useEffect, useMemo, useCallback } from "react"
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Table,
  Badge,
  Tabs,
  Tab,
  InputGroup,
  Dropdown,
  DropdownButton,
  Alert,
  Modal,
  Pagination,
  Nav,
} from "react-bootstrap"
// ReportsDashboard.tsx (or .jsx)
import {
  exportToExcel as utilsExportToExcel,
  exportToPDF   as utilsExportToPDF,
  exportToCSV   as utilsExportToCSV
} from "./utils/exportUtils"

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import { Bar, Line, Pie } from "react-chartjs-2"
import { saveAs } from "file-saver"
import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import "react-datepicker/dist/react-datepicker.css"
import axios from "axios"

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend)

const API_BASE = "http://109.75.164.217:84/api"
const PERIODS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const YEARS = [2023, 2024, 2025]
const GROUPS = ["DCG", "NCG"]

const ReportsDashboard = ({ user, payrollId, group: initialGroup }) => {
  // State for report configuration
  const [activeReportCategory, setActiveReportCategory] = useState("financial")
  const [activeReportType, setActiveReportType] = useState("pension")
  const [dataViewType, setDataViewType] = useState("pension")
  const [isLoading, setIsLoading] = useState(false)
  const [reportData, setReportData] = useState([])
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [searchQuery, setSearchQuery] = useState("")
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "ascending" })
  const [showChartView, setShowChartView] = useState(false)
  const [selectedDateRange, setSelectedDateRange] = useState({ start: null, end: null })
  const [selectedEmployees, setSelectedEmployees] = useState([])
  const [selectedAccounts, setSelectedAccounts] = useState([])
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false)
  const [templateName, setTemplateName] = useState("")
  const [savedTemplates, setSavedTemplates] = useState([])
  const [showScheduleModal, setShowScheduleModal] = useState(false)

  // Predefined dropdown values
  const [period, setPeriod] = useState("")
  const [year, setYear] = useState("")
  const [group, setGroup] = useState(initialGroup)

  const [scheduleConfig, setScheduleConfig] = useState({
    frequency: "weekly",
    day: "monday",
    time: "09:00",
    format: "excel",
    recipients: "",
  })
  const [aggregatedData, setAggregatedData] = useState({
    totalDebit: 0,
    totalCredit: 0,
    employeeCount: 0,
    averageDebit: 0,
  })
  const [activeTab, setActiveTab] = useState("table")
  const columnDisplayNames = {
    employeeNo: "Employee Number",
    employeeName: "Employee Name",
    accountingString: "Acct. String",
    account: "Account",
    descr: "Description",
    debit: "Amount",
    project: "Project",
    credit: "Credit",
    payrollId: "Payroll ID",
    obj: "Object",
    grp: "Group",
  }

  // Add state for API connection status
  const [apiStatus, setApiStatus] = useState({ connected: true, lastChecked: null })

  // Fields to exclude from table display
  const excludedFields = [
    "id",
    "totalHrs",
    "hrs",
    "credit",
    "convertedHors",
    "sysUser",
    "timeStamp",
    "obj",
    "account",
    "descr",
    "payrollId",
    "grp",
  ]

  // Derived state for available filters based on report data
  const availableFilters = useMemo(() => {
    if (!reportData.length) return { employees: [], accounts: [] }

    const employees = [...new Set(reportData.map((item) => `${item.employeeNo} - ${item.employeeName}`))].sort()
    const accounts = [...new Set(reportData.map((item) => item.accountingString))].filter(Boolean).sort()

    return { employees, accounts }
  }, [reportData])

  // Report categories and types configuration
  const reportCategories = [
    {
      id: "financial",
      name: "Financial Reports",
      reports: [
        { id: "pension", name: "Processed Data", endpoint: "/Apportionments/reports/{payrollId}/pension" },
        { id: "apportioned", name: "Apportioned Data", endpoint: "/Apportionments/{group}/{payrollId}" },
        { id: "summary", name: "Financial Summary", endpoint: "/Apportionments/summary/{group}/{payrollId}" },
      ],
    },
    {
      id: "operational",
      name: "Operational Reports",
      reports: [
        { id: "errors", name: "Processing Errors", endpoint: "/Apportionments/errors/{group}/{payrollId}" },
        { id: "activity", name: "User Activity", endpoint: "/Apportionments/activity/{group}/{payrollId}" },
      ],
    },
    {
      id: "compliance",
      name: "Compliance Reports",
      reports: [
        { id: "audit", name: "Audit Trail", endpoint: "/Apportionments/audit/{group}/{payrollId}" },
        { id: "validation", name: "Data Validation", endpoint: "/Apportionments/validation/{group}/{payrollId}" },
      ],
    },
  ]

  // Get current report configuration
  const currentReportCategory = reportCategories.find((cat) => cat.id === activeReportCategory)
  const currentReport = currentReportCategory?.reports.find((report) => report.id === activeReportType)

  // Fetch report data with improved error handling
  const fetchReportData = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      // GET /Apportionments/{type}/{group}/{payrollId}
      const url = `${API_BASE}/Apportionments/reports/${payrollId}/${dataViewType}`
      const { data } = await axios.get(url)
      setReportData(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error("Error loading report data:", err)
      setError(err.message || "Unknown error")
      setReportData([])
    } finally {
      setIsLoading(false)
    }
  }, [payrollId, group, dataViewType])

  // Effect to fetch data when report type changes
  useEffect(() => {
    fetchReportData()
  }, [activeReportCategory, activeReportType, payrollId, group])

  // Sorting logic
  const requestSort = useCallback(
    (key) => {
      let direction = "ascending"
      if (sortConfig.key === key && sortConfig.direction === "ascending") {
        direction = "descending"
      }
      setSortConfig({ key, direction })
    },
    [sortConfig],
  )

  // Filter out excluded fields from the data
  const filteredFields = useMemo(() => {
    if (!reportData.length) return []

    const sampleRow = reportData[0]
    return Object.keys(sampleRow).filter((key) => !excludedFields.includes(key))
  }, [reportData])

  // Apply sorting and filtering to data with memoization for performance
  const filteredAndSortedData = useMemo(() => {
    if (!reportData.length) return []

    let filteredData = [...reportData]

    // Apply search filter
    if (searchQuery) {
      const lowerCaseQuery = searchQuery.toLowerCase()
      filteredData = filteredData.filter((item) =>
        Object.entries(item).some(
          ([key, val]) =>
            !excludedFields.includes(key) &&
            val !== null &&
            val !== undefined &&
            val.toString().toLowerCase().includes(lowerCaseQuery),
        ),
      )
    }

    // Apply employee filter
    if (selectedEmployees.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedEmployees.some((emp) => emp.startsWith(`${item.employeeNo} -`)),
      )
    }

    // Apply account filter
    if (selectedAccounts.length > 0) {
      filteredData = filteredData.filter((item) => selectedAccounts.includes(item.accountingString))
    }

    // Apply date range filter if applicable
    if (selectedDateRange.start && selectedDateRange.end && filteredData.some((item) => item.timeStamp)) {
      filteredData = filteredData.filter((item) => {
        if (!item.timeStamp) return true
        const itemDate = new Date(item.timeStamp)
        return itemDate >= selectedDateRange.start && itemDate <= selectedDateRange.end
      })
    }

    // Apply sorting
    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        if (a[sortConfig.key] === null || a[sortConfig.key] === undefined) return 1
        if (b[sortConfig.key] === null || b[sortConfig.key] === undefined) return -1

        let aValue = a[sortConfig.key]
        let bValue = b[sortConfig.key]

        // Handle numeric values
        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfig.direction === "ascending" ? aValue - bValue : bValue - aValue
        }

        // Convert to strings for comparison
        aValue = aValue.toString().toLowerCase()
        bValue = bValue.toString().toLowerCase()

        // Handle string values
        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1
        }
        return 0
      })
    }

    return filteredData
  }, [reportData, searchQuery, sortConfig, selectedEmployees, selectedAccounts, selectedDateRange, excludedFields])

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredAndSortedData.length / itemsPerPage))
  const paginatedData = filteredAndSortedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  // Build totalsMap: for each employeeNo, store { total, lastTimestamp }
  const totalsMap = useMemo(() => {
    const map = {}
    reportData.forEach((item) => {
      const key = item.employeeNo
      const debit = Number(item.debit) || 0
      const credit = Number(item.credit) || 0
      const ts = item.timeStamp ? new Date(item.timeStamp) : null

      if (!map[key]) {
        map[key] = { total: 0, lastTimestamp: ts }
      }
      map[key].total += debit + credit
      // update lastTimestamp if this record is later
      if (ts && (!map[key].lastTimestamp || ts > map[key].lastTimestamp)) {
        map[key].lastTimestamp = ts
      }
    })
    return map
  }, [reportData])

  // Generate pagination items
  const paginationItems = useMemo(() => {
    const items = []
    for (let number = 1; number <= totalPages; number++) {
      if (number === 1 || number === totalPages || (number >= currentPage - 1 && number <= currentPage + 1)) {
        items.push(
          <Pagination.Item key={number} active={number === currentPage} onClick={() => setCurrentPage(number)}>
            {number}
          </Pagination.Item>,
        )
      } else if (
        (number === currentPage - 2 && currentPage > 3) ||
        (number === currentPage + 2 && currentPage < totalPages - 2)
      ) {
        items.push(<Pagination.Ellipsis key={`ellipsis-${number}`} />)
      }
    }
    return items
  }, [currentPage, totalPages])

  // Chart data preparation with error handling
  const chartData = useMemo(() => {
    if (!reportData.length) return null

    try {
      // For pension reports, show distribution by employee
      if (activeReportType === "pension") {
        const employeeData = {}

        // Group by employee and sum debits
        reportData.forEach((item) => {
          const empKey = item.employeeName || item.employeeNo
          if (!employeeData[empKey]) {
            employeeData[empKey] = 0
          }
          employeeData[empKey] += Number.parseFloat(item.debit) || 0
        })

        // Limit to top 10 employees for better visualization
        const sortedEmployees = Object.entries(employeeData)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)

        const labels = sortedEmployees.map(([name]) => name)
        const data = sortedEmployees.map(([, value]) => value)

        return {
          labels,
          datasets: [
            {
              label: "Processed Data",
              data,
              backgroundColor: [
                "rgba(54, 162, 235, 0.6)",
                "rgba(255, 99, 132, 0.6)",
                "rgba(255, 206, 86, 0.6)",
                "rgba(75, 192, 192, 0.6)",
                "rgba(153, 102, 255, 0.6)",
                "rgba(255, 159, 64, 0.6)",
                "rgba(201, 203, 207, 0.6)",
                "rgba(94, 114, 228, 0.6)",
                "rgba(43, 206, 141, 0.6)",
                "rgba(251, 99, 64, 0.6)",
              ],
              borderColor: [
                "rgba(54, 162, 235, 1)",
                "rgba(255, 99, 132, 1)",
                "rgba(255, 206, 86, 1)",
                "rgba(75, 192, 192, 1)",
                "rgba(153, 102, 255, 1)",
                "rgba(255, 159, 64, 1)",
                "rgba(201, 203, 207, 1)",
                "rgba(94, 114, 228, 1)",
                "rgba(43, 206, 141, 1)",
                "rgba(251, 99, 64, 1)",
              ],
              borderWidth: 1,
            },
          ],
        }
      }

      // Default chart data
      return {
        labels: ["January", "February", "March", "April", "May", "June", "July"],
        datasets: [
          {
            label: "Sample Data",
            data: [65, 59, 80, 81, 56, 55, 40],
            backgroundColor: "rgba(54, 162, 235, 0.6)",
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 1,
          },
        ],
      }
    } catch (error) {
      console.error("Error generating chart data:", error)
      return null
    }
  }, [reportData, activeReportType])

  // Export functions with error handling



// …




  // Template and scheduling functions
  const saveTemplate = useCallback(() => {
    if (!templateName.trim()) {
      alert("Please enter a template name")
      return
    }

    try {
      const newTemplate = {
        id: Date.now(),
        name: templateName,
        config: {
          reportCategory: activeReportCategory,
          reportType: activeReportType,
          filters: {
            employees: selectedEmployees,
            accounts: selectedAccounts,
            dateRange: selectedDateRange,
          },
          sort: sortConfig,
          itemsPerPage,
        },
      }

      const updatedTemplates = [...savedTemplates, newTemplate]
      setSavedTemplates(updatedTemplates)
      setShowSaveTemplateModal(false)
      setTemplateName("")

      // In a real app, you would save this to a database or localStorage
      localStorage.setItem("reportTemplates", JSON.stringify(updatedTemplates))
    } catch (error) {
      console.error("Error saving template:", error)
      alert(`Error saving template: ${error.message}`)
    }
  }, [
    templateName,
    activeReportCategory,
    activeReportType,
    selectedEmployees,
    selectedAccounts,
    selectedDateRange,
    sortConfig,
    itemsPerPage,
    savedTemplates,
  ])

  const loadTemplate = useCallback((template) => {
    try {
      setActiveReportCategory(template.config.reportCategory)
      setActiveReportType(template.config.reportType)
      setSelectedEmployees(template.config.filters.employees || [])
      setSelectedAccounts(template.config.filters.accounts || [])
      setSelectedDateRange(template.config.filters.dateRange || { start: null, end: null })
      setSortConfig(template.config.sort || { key: "id", direction: "ascending" })
      setItemsPerPage(template.config.itemsPerPage || 10)
    } catch (error) {
      console.error("Error loading template:", error)
      alert(`Error loading template: ${error.message}`)
    }
  }, [])

  const scheduleReport = useCallback(() => {
    try {
      // In a real app, this would send the schedule to a backend service
      alert(`Report scheduled: ${scheduleConfig.frequency} on ${scheduleConfig.day} at ${scheduleConfig.time}`)
      setShowScheduleModal(false)
    } catch (error) {
      console.error("Error scheduling report:", error)
      alert(`Error scheduling report: ${error.message}`)
    }
  }, [scheduleConfig])

  // Load saved templates from localStorage on component mount
  useEffect(() => {
    try {
      const storedTemplates = localStorage.getItem("reportTemplates")
      if (storedTemplates) {
        setSavedTemplates(JSON.parse(storedTemplates))
      }
    } catch (error) {
      console.error("Error loading saved templates:", error)
    }
  }, [])

  return (
    <Card className="shadow-sm mb-4 border-0">
     

     <Card.Body className="pt-0">
      
     <Row className="mb-3">
  <Col>
    <Form.Group
      className="position-relative"
      style={{ maxWidth: 300 }}
    >
      {/* your 300px-wide select */}
      <Form.Select
        size="sm"
        value={dataViewType}
        onChange={(e) => setDataViewType(e.target.value)}
        disabled={isLoading}
      >
        <option value="pension">Pension</option>
        <option value="payroll">Payroll</option>
        <option value="airtime">Airtime</option>
        <option value="accrleave">Accrleave</option>
      </Form.Select>

      {/* absolutely positioned at bottom-right of the 300px box */}
      <Button
        size="sm"
        variant="primary"
        onClick={fetchReportData}
        disabled={isLoading || !dataViewType}
        className="position-absolute end-0 bottom-0"
      >
        {isLoading ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" />
            Loading…
          </>
        ) : (
          <>
            <i className="bi bi-search me-2" />
            Search
          </>
        )}
      </Button>
    </Form.Group>
  </Col>
</Row>


        {/* Search and Filters */}

        {/* Report Content */}
        <>
          {/* <Card.Header className="bg-white py-3">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-primary">
                <i className="bi bi-file-earmark-text me-2"></i>
                {currentReport?.name || "Report"}
              </h5>
              <div className="d-flex align-items-center">
                <Badge bg="light" text="dark" className="me-3 py-2">
                  <i className="bi bi-filter me-1"></i>
                  Filtered: {filteredAndSortedData.length} of {reportData.length} records
                </Badge>
                <DropdownButton
                  id="export-dropdown"
                  variant="outline-primary"
                  title={
                    <>
                      <i className="bi bi-download me-1" />
                      Export
                    </>
                  }
                  size="sm"
                >
                  <Dropdown.Item onClick={exportToExcel}>
                    <i className="bi bi-file-earmark-excel me-2" /> Excel
                  </Dropdown.Item>
                  <Dropdown.Item onClick={exportToCSV}>
                    <i className="bi bi-file-earmark-spreadsheet me-2" /> CSV
                  </Dropdown.Item>
                  <Dropdown.Item onClick={exportToPDF}>
                    <i className="bi bi-file-earmark-pdf me-2" /> PDF
                  </Dropdown.Item>
                </DropdownButton>
              </div>
            </div>
          </Card.Header> */}

          <Card.Body>
            {error ? (
              <Alert variant="danger" className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill me-2 fs-5"></i>
                <div>{error}</div>
              </Alert>
            ) : isLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading report data...</p>
              </div>
            ) : reportData.length === 0 ? (
              <Alert variant="info" className="d-flex align-items-center">
                <i className="bi bi-info-circle-fill me-2 fs-5"></i>
                <div>No report data found for the selected criteria.</div>
              </Alert>
            ) : (
              <>
                {/* View Tabs */}
              

             
         
                  <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                      <InputGroup className="w-50" style={{ maxWidth: 300 }}>
                        <InputGroup.Text className="bg-light">
                          <i className="bi bi-search"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search in all columns..."
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value)
                            setCurrentPage(1)
                          }}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => {
                            setSelectedEmployees([])
                            setSelectedAccounts([])
                            setSelectedDateRange({ start: null, end: null })
                            setSearchQuery("")
                            setSortConfig({ key: "id", direction: "ascending" })
                          }}
                        >
                          <i className="bi bi-x-circle me-1"></i>
                          Clear
                        </Button>
                      </InputGroup>
                      <div>
                      <DropdownButton
  id="export-dropdown"
  variant="outline-primary"
  title={
    <>
      <i className="bi bi-download me-1" />
      Export
    </>
  }
  size="sm"
  drop="up"
  align="end"
>
  <Dropdown.Item
    onClick={() =>
      utilsExportToExcel({
        title: currentReport?.name || "Report",
        id: payrollId,
        rows: filteredAndSortedData,
        headers: filteredFields,
      })
    }
  >
    <i className="bi bi-file-earmark-excel me-2" />
    Excel
  </Dropdown.Item>

  <Dropdown.Item
    onClick={() =>
      utilsExportToCSV({
        title: currentReport?.name || "Report",
        id: payrollId,
        rows: filteredAndSortedData,
        headers: filteredFields,
      })
    }
  >
    <i className="bi bi-file-earmark-spreadsheet me-2" />
    CSV
  </Dropdown.Item>

  <Dropdown.Item
    onClick={() =>
      utilsExportToPDF({
        title: currentReport?.name || "Report",
        id: payrollId,
        rows: filteredAndSortedData,
        headers: filteredFields,
      })
    }
  >
    <i className="bi bi-file-earmark-pdf me-2" />
    PDF
  </Dropdown.Item>
</DropdownButton>


                      </div>
                    </div>
                    <div style={{ maxHeight: "100vh", overflowY: "auto" }} className="border rounded">
                      <Table striped hover responsive className="mb-0">
                        <thead className="sticky-top bg-light">
                          <tr>
                            {filteredFields.map((key) => (
                              <th
                                key={key}
                                onClick={() => requestSort(key)}
                                className="position-relative py-3"
                                style={{ cursor: "pointer" }}
                              >
                                {columnDisplayNames[key] || key}
                                {sortConfig.key === key && (
                                  <i
                                    className={`bi bi-caret-${
                                      sortConfig.direction === "ascending" ? "up" : "down"
                                    }-fill ms-1`}
                                  />
                                )}
                              </th>
                            ))}
                          </tr>
                          
                        </thead>
                        <tbody>
                          {paginatedData.length === 0 ? (
                            <tr>
                              <td colSpan={filteredFields.length} className="text-center py-5">
                                <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                                No records found matching your filters
                              </td>
                            </tr>
                          ) : (
                            paginatedData.map((row, idx) => (
                              <tr key={idx}>
                                {filteredFields.map((key) => (
                                  <td key={key}>
                                    {key === "timeStamp" && row[key]
                                      ? new Date(row[key]).toLocaleString()
                                      : typeof row[key] === "number"
                                        ? ["debit", "credit"].includes(key.toLowerCase())
                                          ? row[key].toLocaleString(undefined, {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })
                                          : row[key].toLocaleString()
                                        : row[key]}
                                  </td>
                                ))}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="small text-muted">
                        Showing {paginatedData.length} of {filteredAndSortedData.length} entries
                      </div>
                      <Pagination>
                        <Pagination.Prev
                          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                        />
                        {paginationItems}
                        <Pagination.Next
                          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                        />
                      </Pagination>
                    </div>
                  </>
                
              </>
            )}
          </Card.Body>
        </>
      </Card.Body>

      {/* Save Template Modal */}
      <Modal show={showSaveTemplateModal} onHide={() => setShowSaveTemplateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Save Report Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Template Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter a name for this template"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
              <Form.Text className="text-muted">
                This will save your current report type, filters, and sort settings.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveTemplateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveTemplate}>
            Save Template
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Schedule Report Modal */}
      <Modal show={showScheduleModal} onHide={() => setShowScheduleModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Schedule Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Frequency</Form.Label>
              <Form.Select
                value={scheduleConfig.frequency}
                onChange={(e) => setScheduleConfig({ ...scheduleConfig, frequency: e.target.value })}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </Form.Select>
            </Form.Group>

            {scheduleConfig.frequency === "weekly" && (
              <Form.Group className="mb-3">
                <Form.Label>Day of Week</Form.Label>
                <Form.Select
                  value={scheduleConfig.day}
                  onChange={(e) => setScheduleConfig({ ...scheduleConfig, day: e.target.value })}
                >
                  <option value="monday">Monday</option>
                  <option value="tuesday">Tuesday</option>
                  <option value="wednesday">Wednesday</option>
                  <option value="thursday">Thursday</option>
                  <option value="friday">Friday</option>
                  <option value="saturday">Saturday</option>
                  <option value="sunday">Sunday</option>
                </Form.Select>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Time</Form.Label>
              <Form.Control
                type="time"
                value={scheduleConfig.time}
                onChange={(e) => setScheduleConfig({ ...scheduleConfig, time: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Format</Form.Label>
              <Form.Select
                value={scheduleConfig.format}
                onChange={(e) => setScheduleConfig({ ...scheduleConfig, format: e.target.value })}
              >
                <option value="excel">Excel</option>
                <option value="pdf">PDF</option>
                <option value="csv">CSV</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Recipients (Email)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter email addresses separated by commas"
                value={scheduleConfig.recipients}
                onChange={(e) => setScheduleConfig({ ...scheduleConfig, recipients: e.target.value })}
              />
              <Form.Text className="text-muted">
                The report will be sent to these email addresses on the scheduled date and time.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowScheduleModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={scheduleReport}>
            Save Report
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  )
}

export default ReportsDashboard
